import { createSlice } from '@reduxjs/toolkit';
import type { UnLocode } from '../../services/api/unLocodeService';
import {
  fetchUnLocodes, fetchActiveUnLocodes,
  createUnLocode, updateUnLocode, deleteUnLocode,
  activateUnLocode, deactivateUnLocode,
} from '../thunks/unLocodeThunks';

export interface UnLocodeState {
  unLocodes: UnLocode[];
  activeUnLocodes: UnLocode[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: UnLocodeState = {
  unLocodes: [],
  activeUnLocodes: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
};

const unLocodeSlice = createSlice({
  name: 'unLocode',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // --- Fetch UN/LOCODEs (admin, paginated) ---
    builder
      .addCase(fetchUnLocodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUnLocodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.unLocodes = action.payload.unLocodes;
        state.totalCount = action.payload.totalCount;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchUnLocodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Fetch Active UN/LOCODEs (dropdown) ---
    builder
      .addCase(fetchActiveUnLocodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveUnLocodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeUnLocodes = action.payload;
      })
      .addCase(fetchActiveUnLocodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Create UN/LOCODE ---
    // Note: rejected errors are handled locally via .unwrap() in the component (formError)
    builder
      .addCase(createUnLocode.pending, (state) => { state.isLoading = true; })
      .addCase(createUnLocode.fulfilled, (state) => { state.isLoading = false; })
      .addCase(createUnLocode.rejected, (state) => { state.isLoading = false; });

    // --- Update UN/LOCODE ---
    // Note: rejected errors are handled locally via .unwrap() in the component (formError)
    builder
      .addCase(updateUnLocode.pending, (state) => { state.isLoading = true; })
      .addCase(updateUnLocode.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updateUnLocode.rejected, (state) => { state.isLoading = false; });

    // --- Delete UN/LOCODE ---
    builder
      .addCase(deleteUnLocode.fulfilled, (state, action) => {
        state.unLocodes = state.unLocodes.filter(c => c.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });

    // --- Activate / Deactivate (optimistic) ---
    builder
      .addCase(activateUnLocode.pending, (state, action) => {
        const code = state.unLocodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = true; code.status = 'active'; }
      })
      .addCase(activateUnLocode.fulfilled, (state, action) => {
        const code = state.unLocodes.find(c => c.id === action.payload);
        if (code) { code.isActive = true; code.status = 'active'; }
      })
      .addCase(activateUnLocode.rejected, (state, action) => {
        const code = state.unLocodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateUnLocode.pending, (state, action) => {
        const code = state.unLocodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateUnLocode.fulfilled, (state, action) => {
        const code = state.unLocodes.find(c => c.id === action.payload);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateUnLocode.rejected, (state, action) => {
        const code = state.unLocodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = true; code.status = 'active'; }
      });
  },
});

export const { clearError } = unLocodeSlice.actions;
export default unLocodeSlice.reducer;
